import { css } from '@emotion/react';
import { useRouter } from 'next/router';

import { Colors, Link, Spinner, Typography } from '@zapier/design-system';
import { urls, useSize } from '@zapier/identity-utils';
import { Button } from '@zapier/marketing-ui';
import { useTranslations } from 'next-intl';

import {
  AuthLayout,
  CompaniesIntroPromo,
} from '@zapier/auth-pages/src/components/global';

import { useVerifySignup } from './useVerifySignup';

const rootStyle = css`
  display: grid;
  gap: 25px;
  justify-items: center;
  padding: 20px 0;
`;

const textStyle = css`
  ${Typography.Body1}
  color: ${Colors.GrayWarm10};
  text-align: center;
`;

const errorStyle = css`
  ${Typography.Body1}
  color: ${Colors.StatusErrorWeaker};
  text-align: center;
`;

const linkRowStyle = css`
  ${Typography.Body1}
  text-align: center;
`;

export function VerifySignupPage() {
  const router = useRouter();
  const size = useSize();
  const t = useTranslations('verify-signup');

  // Token comes from the path; wait for `isReady` before reading `router.query`.
  const token = (router.query.token as string | undefined) ?? '';
  const { status, activate } = useVerifySignup(token);

  if (!router.isReady) {
    return null;
  }

  function content() {
    switch (status) {
      // Peek / redirect in flight — never auto-activate.
      case 'peeking':
      case 'activating':
      case 'redirecting':
        return <Spinner />;

      case 'wrong-state':
        return (
          <>
            <p css={textStyle}>{t('wrong-state')}</p>
            <p css={linkRowStyle}>
              <Link href={urls.LOGIN_URL}>{t('go-to-login')}</Link>
            </p>
          </>
        );

      case 'rate-limited':
        return <p css={errorStyle}>{t('rate-limited')}</p>;

      // Recoverable failure: show the error and a retry button, nothing else.
      case 'error':
        return (
          <>
            <p css={errorStyle}>{t('error')}</p>
            <Button
              variant="brand"
              fullWidth={true}
              onClick={() => void activate()}
            >
              {t('try-again')}
            </Button>
          </>
        );

      case 'pending':
      default:
        return (
          <>
            <p css={textStyle}>{t('description')}</p>
            <Button
              variant="brand"
              fullWidth={true}
              onClick={() => void activate()}
            >
              {t('activate-button')}
            </Button>
          </>
        );
    }
  }

  return (
    <AuthLayout
      promo={<CompaniesIntroPromo />}
      showPromo={size !== 'small'}
      title={t('title')}
      titlePosition="right"
    >
      <section css={rootStyle}>{content()}</section>
    </AuthLayout>
  );
}
